import React from 'react'
import {
  FolderArrowDownIcon,
  CodeBracketIcon,
  DocumentTextIcon,
  ArrowTopRightOnSquareIcon,
  CheckCircleIcon,
} from '@heroicons/react/24/outline'
import { FaGithub } from 'react-icons/fa'

/**
 * StarterCodeSection Component
 * 
 * Displays starter code/repository information for capstone projects
 * Provides download links, setup instructions, and GitHub repository access
 */
const StarterCodeSection = ({ capstone }) => {
  if (!capstone.starter_repo_url && !capstone.project_path) {
    return null
  }

  // Extract repo info from GitHub URL
  const getGitHubRepoInfo = (url) => {
    const match = url?.match(/github\.com\/([^\/]+)\/([^\/\?#]+)/)
    if (match) {
      return {
        owner: match[1],
        repo: match[2].replace('.git', ''),
        fullName: `${match[1]}/${match[2].replace('.git', '')}`,
      }
    }
    return null
  }

  const repoInfo = getGitHubRepoInfo(capstone.starter_repo_url)

  return (
    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-6 space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3 pb-3 border-b border-blue-200">
        <div className="p-2 bg-blue-600 rounded-lg">
          <FolderArrowDownIcon className="h-6 w-6 text-white" />
        </div>
        <div>
          <h3 className="text-lg font-bold text-gray-900">Starter Code & Resources</h3>
          <p className="text-sm text-gray-600">
            Get started with the project template and example code
          </p>
        </div>
      </div>

      {/* GitHub Repository Section */}
      {capstone.starter_repo_url && repoInfo && (
        <div className="bg-white border border-gray-200 rounded-lg p-5 space-y-4">
          {/* Repository Card */}
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <FaGithub className="h-8 w-8 text-gray-700 flex-shrink-0" />
              <div>
                <a
                  href={capstone.starter_repo_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-lg font-bold text-blue-600 hover:text-blue-700 hover:underline flex items-center gap-2"
                >
                  {repoInfo.fullName}
                  <ArrowTopRightOnSquareIcon className="h-4 w-4" />
                </a>
                <p className="text-sm text-gray-600 mt-1">
                  Official starter repository
                </p>
              </div>
            </div>
            <span className="px-3 py-1 bg-green-100 text-green-700 text-xs font-semibold rounded-full">
              STARTER CODE
            </span>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {/* Clone with HTTPS */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 block">
                Clone with HTTPS
              </label>
              <div className="flex items-center gap-2">
                <code className="flex-1 text-sm bg-white px-3 py-2 rounded border border-gray-300 text-gray-800 font-mono overflow-x-auto">
                  {capstone.starter_repo_url}
                </code>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(`git clone ${capstone.starter_repo_url}`)
                    alert('Copied to clipboard!')
                  }}
                  className="px-3 py-2 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 whitespace-nowrap"
                >
                  Copy
                </button>
              </div>
            </div>

            {/* Direct Download */}
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
              <label className="text-xs font-semibold text-gray-700 uppercase tracking-wide mb-2 block">
                Download ZIP
              </label>
              <a
                href={`${capstone.starter_repo_url}/archive/refs/heads/main.zip`}
                className="flex items-center justify-center gap-2 w-full px-4 py-2 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 transition-colors"
              >
                <FolderArrowDownIcon className="h-4 w-4" />
                Download Project Files
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div className="flex flex-wrap gap-2 pt-2">
            <a
              href={`${capstone.starter_repo_url}/tree/main`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors"
            >
              <CodeBracketIcon className="h-4 w-4" />
              Browse Code
            </a>
            <a
              href={`${capstone.starter_repo_url}#readme`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors"
            >
              <DocumentTextIcon className="h-4 w-4" />
              View README
            </a>
            <a
              href={`${capstone.starter_repo_url}/issues`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 hover:border-gray-400 transition-colors"
            >
              💬 Ask Questions
            </a>
          </div>
        </div>
      )}

      {/* Setup Instructions */}
      {capstone.setup_instructions && (
        <div className="bg-white border border-blue-200 rounded-lg p-5">
          <div className="flex items-center gap-2 mb-3">
            <DocumentTextIcon className="h-5 w-5 text-blue-600" />
            <h4 className="text-md font-semibold text-gray-900">Setup Instructions</h4>
          </div>
          <div className="prose prose-sm max-w-none text-gray-700">
            <div dangerouslySetInnerHTML={{ __html: capstone.setup_instructions }} />
          </div>
        </div>
      )}

      {/* Getting Started Checklist */}
      <div className="bg-white border border-blue-200 rounded-lg p-5">
        <div className="flex items-center gap-2 mb-4">
          <CheckCircleIcon className="h-5 w-5 text-blue-600" />
          <h4 className="text-md font-semibold text-gray-900">Getting Started Checklist</h4>
        </div>
        <ol className="space-y-3 text-sm text-gray-700">
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold">
              1
            </span>
            <span>
              <strong className="font-semibold">Clone the repository</strong> or download the ZIP file
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold">
              2
            </span>
            <span>
              <strong className="font-semibold">Read the README</strong> for setup instructions and requirements
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold">
              3
            </span>
            <span>
              <strong className="font-semibold">Install dependencies</strong> and set up your environment
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold">
              4
            </span>
            <span>
              <strong className="font-semibold">Review the starter code</strong> and understand the structure
            </span>
          </li>
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center text-xs font-bold">
              5
            </span>
            <span>
              <strong className="font-semibold">Start building!</strong> Follow the requirements and deliverables
            </span>
          </li>
        </ol>
      </div>

      {/* Help Section */}
      <div className="bg-blue-100 border border-blue-300 rounded-lg p-4">
        <p className="text-sm text-blue-900">
          <strong className="font-semibold">💡 Need Help?</strong> If you encounter issues with the starter code,
          check the repository's Issues tab or ask in the discussion board. Make sure to read the README carefully
          for setup requirements and troubleshooting tips.
        </p>
      </div>
    </div>
  )
}

export default StarterCodeSection
